import { GameEventDto } from 'modules/games/domain/interfaces/game-event-dto';
import { deleteGameEvent, editGameEvent } from 'modules/games/store/actions';

import { ChampionshipSettings } from 'modules/championship/domain/interfaces/championship-settings';
import { championshipSettingsSelector } from 'modules/championship/store/championship/selectors';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';

import { GameDetails, GameDetailsTeamMember, GameEvent, GameEventType } from 'modules/games/domain/interfaces/game-details';
import { getPeriodsSettings } from 'modules/games/domain/utils/match-periods-settings';
import { getMembers } from 'modules/games/utils/get-members';
import { GameEventForm } from '../game-event-form';
import { GameResultEvent } from './game-result-event';
import * as styles from './game-result-events.list.styles';
import { GameResultEventPeriod } from './game-result-events.list.styles';

type GameEventsListProps = {
  className?: string;
  isLoading: boolean;
  game: GameDetails | null;
  championshipId: number;
};

export const GameResultEventsList = (props: GameEventsListProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const settings = useSelector(championshipSettingsSelector(props.championshipId));
  const periodSettings = getPeriodsSettings(settings || ({} as ChampionshipSettings));

  const [isEditOpen, setIsEditOpen] = useState(false);
  const open = useCallback(() => setIsEditOpen(true), []);
  const close = useCallback(() => setIsEditOpen(false), []);

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const openDeleteModal = useCallback(() => setIsDeleteOpen(true), []);
  const closeDeleteModal = useCallback(() => setIsDeleteOpen(false), []);

  const [editData, setEditData] = useState<
    | {
        values: {
          id: number;
          teamId: number;
          type?: GameEventType;
          period?: number;
          seconds?: number;
          firstPlayerId?: number;
          secondPlayerId?: number;
          videoUrl?: string;
        };
        members: GameDetailsTeamMember[];
      }
    | undefined
  >();

  const { className, isLoading, game } = props;

  const onEditClick = useCallback(
    (eventId: number) => {
      const events = game ? game.events : [];
      const event = events.find((e) => e.id === eventId);

      if (event) {
        const teamIndex = game!.teams.findIndex((team) => team.id === event.teamId);

        setEditData({
          values: {
            id: event.id,
            teamId: event.teamId,
            type: event.type,
            period: event.period,
            seconds: event.seconds,
            firstPlayerId: event.playerIds[0] ?? undefined,
            secondPlayerId: event.playerIds[1] ?? undefined,
            videoUrl: event.videoUrl,
          },
          members: game!.teams[teamIndex].members,
        });

        open();
      }
    },
    [game, setEditData, open],
  );

  const handleSubmit = useCallback(
    (data: GameEventDto) => {
      dispatch(
        editGameEvent({
          data,
          periodSettings,
        }),
      );
      close();
    },
    [close, dispatch, periodSettings],
  );

  const handleDeleteEvent = useCallback(() => {
    close();
    openDeleteModal();
  }, [close, openDeleteModal]);

  const editEventForm = useMemo(() => {
    if (!editData) {
      return null;
    }

    return (
      <GameEventForm initialValues={editData.values} members={editData.members} onSubmit={handleSubmit} onDelete={handleDeleteEvent} />
    );
  }, [editData, handleDeleteEvent, handleSubmit]);

  const confirmDeleteEvent = useCallback(() => {
    if (!editData) {
      return;
    }

    dispatch(deleteGameEvent(editData.values.id));
    closeDeleteModal();
  }, [editData, dispatch, closeDeleteModal]);

  const cancelDeleteEvent = useCallback(() => {
    closeDeleteModal();
    open();
  }, [closeDeleteModal, open]);

  const isEmptyList = !isLoading && !(game && game.events.length);
  const events = !isLoading && game ? game.events : [];
  const members = game?.teams ? getMembers(game.teams) : {};

  const isLeftEvent = (event: GameEvent) => event.teamId === game?.teams[0].id;

  return (
    <styles.GameEventsList className={className}>
      {isEmptyList && <div className="empty-list">{t('games.emptyGameResultEvents')}</div>}
      {events.map((event, i) => (
        <>
          {events[i - 1]?.period !== event.period && (
            <GameResultEventPeriod>
              <div className="period-title">{t(`games.periods.${event.period}`)}</div>
            </GameResultEventPeriod>
          )}
          <GameResultEvent key={event.id} event={event} members={members} isLeft={isLeftEvent(event)} onEditClick={onEditClick} />
        </>
      ))}
      <Modal show={isEditOpen} onClose={close}>
        <Modal.Header title="Редактировать событие матча" />
        <Modal.Body className="min-w-[500px]">
          {editEventForm}
        </Modal.Body>
      </Modal>
      <Modal show={isDeleteOpen} onClose={cancelDeleteEvent}>
        <Modal.Header title="Подтвердить удаление" subtitle="Вы действительно хотите удалить событие?" />
        <Modal.Footer justify="between">
          <UDButton variant="secondary" className="mb-3" type="button" onClick={cancelDeleteEvent}>
            Отмена
          </UDButton>

          <UDButton variant="primary" className="mb-3" type="button" onClick={confirmDeleteEvent}>
            Удалить
          </UDButton>
        </Modal.Footer>
      </Modal>
    </styles.GameEventsList>
  );
};
